import React, { useEffect } from 'react'
import styled from 'styled-components'
import { RouteComponentProps, useLocation } from 'react-router-dom'
import {
  DynamicLoadingPage,
  NotFoundView,
  usePreviewPwd,
} from '@sketch/modules-common'
import { PreviewLayout } from './PreviewLayout'
import { PageCanvasView } from './PageCanvasView'
import { PasswordProtectedScreen } from './PasswordProtectedScreen'
import { PreviewContextProvider } from './PreviewContext'
import { ShareViewsNavigationContextProvider } from '../../utils/shareViewsNavigation'
import ShareSidebarTabContext from '../../components/ShareSidebarTabContext'
import { useGetPreviewDetailsLazyQuery } from '@sketch/gql-types'
import DocumentView from '../../DocumentView'
import { ReactComponent as MagnifyingGlass } from '@sketch/icons/document-magnifying-glass-64'
import { Text } from '@sketch/components'

type PreviewViewProps = RouteComponentProps<{
  previewID: string
}>

const DocumentIcon = styled(MagnifyingGlass)`
  width: 64px;
  height: 64px;
  flex: none;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

export const PreviewView: React.FC<PreviewViewProps> = ({ match }) => {
  const location = useLocation()

  const { previewID } = match.params

  const password = usePreviewPwd(previewID)

  const [getPreviewDetailsQuery, { data, loading, error }] =
    useGetPreviewDetailsLazyQuery()

  // loads the data on mount
  useEffect(() => {
    getPreviewDetailsQuery({
      variables: { previewIdentifier: previewID },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // reloads when password updates
  useEffect(() => {
    getPreviewDetailsQuery({
      variables: { previewIdentifier: previewID, password },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password])

  if (loading) {
    return <DynamicLoadingPage />
  }

  const hasForbiddenError = error?.graphQLErrors?.some(
    error => error.extensions?.code === 'FORBIDDEN'
  )

  const preview = data?.preview

  if (hasForbiddenError && !preview) {
    return <PasswordProtectedScreen previewID={previewID} />
  }

  if (error || !preview) {
    return (
      <NotFoundView
        title="We cannot find this preview."
        additionalMessage={
          <Text textStyle="copy.quaternary.standard.E" pb={4}>
            Please check the URL with the person that shared it with you. It
            might have been deleted or moved.
          </Text>
        }
        customIcon={<DocumentIcon />}
        hideLink
      />
    )
  }

  const isPrototypesRoute = location.pathname.includes('/prototypes')
  const title = preview.name

  return (
    <ShareViewsNavigationContextProvider>
      <PreviewContextProvider preview={preview} password={password}>
        <ShareSidebarTabContext>
          <PreviewLayout
            title={title}
            render={layoutProps =>
              isPrototypesRoute ? (
                <DocumentView {...layoutProps} />
              ) : (
                <PageCanvasView preview={preview} {...layoutProps} />
              )
            }
          />
        </ShareSidebarTabContext>
      </PreviewContextProvider>
    </ShareViewsNavigationContextProvider>
  )
}
